import React, {
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import classNames from 'classnames';
import TrashIcon from '../../../../icons/Trash.icon';
import useCallbackRef from '../../../../hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { Button, Input, Modal } from 'ncoded-component-library';
import {
  fileJsxWithTypeOpened,
  fileJsxWithType,
  DropzoneFile,
  FileProps,
  FileType,
} from '../../utils';
import confirm from '../../../../modules/confirm';
import { useTranslation } from 'react-i18next';
import {
  CommonFileAction,
  DropzoneProps,
} from 'components/Dropzone/Dropzone.component';
import useDebouncedState from 'hooks/useDebouncedState';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { SUCCESS_MESSAGES } from 'request-responses';
import { sentryError } from 'utils';
import { getImgUrl } from 'components/ResolveImage/utils';
import PencilIcon from 'icons/Pencil.icon';
import CannotPreviewInBrowser from 'components/CannotPreviewInBrowser';
import { SIZE } from 'models/Size';

import './DisplayDropzoneFile.styles.scss';

export const Image: React.FC<FileProps> = ({ name, url, className }) => (
  <img src={getImgUrl(url, '720')} alt={name} className={className} />
);

export const Video: React.FC<FileProps & { controls?: boolean }> = ({
  url,
  className,
  controls,
}) => <video src={url} className={className} controls={controls} />;

export const DOCX_TYPE_SUFIX = 'msword';
export const PDF_TYPE_SUFIX = 'pdf';

export const Application: React.FC<FileProps> = React.memo(
  ({ name, url, className, typeSufix, triggerDownload = false }) => {
    const classes = classNames(
      'application-preview',
      { 'not-pdf': typeSufix !== PDF_TYPE_SUFIX },
      className,
    );

    const iframe = (
      <iframe key={url} src={url} title={name} className={classes} />
    );

    if (typeSufix === PDF_TYPE_SUFIX) return iframe;

    return (
      <CannotPreviewInBrowser
        size={triggerDownload ? SIZE.BIG : SIZE.SMALL}
        showWeTriggerText={triggerDownload}
      >
        {triggerDownload && iframe}
      </CannotPreviewInBrowser>
    );
  },
  (prev, next) => prev.url === next.url,
);

export const ApplicationImage: React.FC<FileProps> = ({
  name,
  url,
  className,
}) => {
  const { t } = useTranslation();

  const clases = classNames('application-image', className);

  return (
    <div key={url} className={clases} title={name}>
      <p>{t('fileSuccessfullyUploaded')}</p>
      <p>{t('clickToPreview')}</p>
    </div>
  );
};

type DisplayDropzoneFileProps = {
  className?: string;
  type: string;
  fileKey: string;
  id: string;
  hasCustomNames?: boolean;
  showApplicationAsIframe?: boolean;
  commonFileAction?: CommonFileAction;
  replaceFile?: boolean;
  disabled?: boolean;
  onFileDelete: (file: DropzoneFile) => void;
  onReplace: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  updateCustomFileName: (
    fileId: string,
    customFileName: string,
  ) => Promise<void>;
} & FileProps &
  DropzoneFile &
  Pick<DropzoneProps, 'deleteMessage'>;

const DisplayDropzoneFile: React.FC<DisplayDropzoneFileProps> = (props) => {
  const {
    className,
    children,
    fileKey: key,
    deleteMessage,
    customFileName,
    hasCustomNames,
    disabled,
    showApplicationAsIframe,
    commonFileAction,
    replaceFile,
    onReplace,
    onFileDelete,
    updateCustomFileName,
    ...dropzoneFile
  } = props;

  const { type: propType, id, ...rest } = dropzoneFile;

  const [modal, modalRef] = useCallbackRef<OverlayRef>();
  const { t } = useTranslation();
  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const [type, typeSufix] = useMemo(
    () => propType.split('/') as [FileType, string | undefined],
    [propType],
  );

  const classes = classNames(
    'resolve-display-dropzone-file',
    { 'has-custom-names': hasCustomNames },
    className,
  );

  const modalClasses = classNames('resolve-display-dropzone-file__modal', {
    'iframe-modal': ['application', 'text', 'message'].includes(type),
  });

  const [debouncedInputValue, debouncedSetState] = useDebouncedState<string>({
    debounceTime: 700,
  });
  const [inputValue, setInputValue] = useState(customFileName);

  useEffect(() => {
    if (
      !debouncedInputValue ||
      !updateCustomFileName ||
      debouncedInputValue === customFileName
    )
      return;

    (async () => {
      try {
        await updateCustomFileName(id, debouncedInputValue);
        popSuccess(
          `${t(SUCCESS_MESSAGES.FILE_NAME_UPDATED)} ${debouncedInputValue}`,
        );
      } catch (e) {
        sentryError(e);
        setInputValue(customFileName);
        debouncedSetState('');
        popServerError(e);
      }
    })();
  }, [
    id,
    customFileName,
    debouncedInputValue,
    debouncedSetState,
    popServerError,
    popSuccess,
    t,
    updateCustomFileName,
  ]);

  const FilePreview = useMemo(() => {
    if (!fileJsxWithType[type]) return (() => null) as React.FC;

    return showApplicationAsIframe
      ? fileJsxWithTypeOpened[type]
      : fileJsxWithType[type];
  }, [showApplicationAsIframe, type]);

  const FilePreviewOpened = useMemo(() => {
    if (!fileJsxWithTypeOpened[type]) return (() => null) as React.FC;

    return fileJsxWithTypeOpened[type];
  }, [type]);

  const handleDelete = useCallback(
    async (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      ev.stopPropagation();
      ev.preventDefault();
      if (
        await confirm(
          deleteMessage || {
            title: t('deleteFile'),
            content: t('areYouSureYouWantToDeleteThisFile'),
          },
          true,
        )
      ) {
        onFileDelete({ ...dropzoneFile, key } as DropzoneFile);
      }
    },
    [deleteMessage, dropzoneFile, key, onFileDelete, t],
  );

  const handleReplace = useCallback(
    (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      onReplace(ev);
      ev.stopPropagation();
      ev.preventDefault();
    },
    [onReplace],
  );

  return (
    <div className={classes}>
      {hasCustomNames && (
        <Input
          className="resolve-display-dropzone-file__custom-name-input"
          value={inputValue}
          onChange={(ev) => {
            setInputValue(ev.target.value);
            debouncedSetState(ev.target.value);
          }}
        />
      )}
      <FilePreview
        {...rest}
        typeSufix={typeSufix}
        className="resolve-display-dropzone-file__file"
      />
      <div
        className="resolve-display-dropzone-file__overlay"
        onClick={() => modal?.open()}
      >
        {!disabled && (
          <Button
            variant="icon"
            className="action"
            icon={replaceFile ? PencilIcon : TrashIcon}
            onClick={replaceFile ? handleReplace : handleDelete}
          />
        )}

        {commonFileAction && !disabled && (
          <Button
            className="custom-action"
            variant="icon"
            icon={commonFileAction?.icon}
            onClick={async (ev) => {
              ev.stopPropagation();
              ev.preventDefault();
              commonFileAction?.action({ customFileName, ...dropzoneFile });
            }}
          />
        )}
      </div>
      <Modal className={modalClasses} ref={modalRef}>
        <FilePreviewOpened
          {...rest}
          typeSufix={typeSufix}
          controls
          className="resolve-display-dropzone-file__file"
          triggerDownload
        />
      </Modal>
    </div>
  );
};

export default DisplayDropzoneFile;
